import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useTenant } from "@/lib/tenant-context";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { Home, CreditCard, User, Bell } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import UserHome from "@/components/user-app/UserHome";
import UserCard from "@/components/user-app/UserCard";
import UserProfile from "@/components/user-app/UserProfile";
import UserKyc from "@/components/user-app/UserKyc";
import UserNotifications from "@/components/user-app/UserNotifications";

const TABS = [
  { id: "home", label: "Home", icon: Home },
  { id: "card", label: "Account", icon: CreditCard },
] as const;

type TabId = typeof TABS[number]["id"] | "profile" | "notifications";

const UserAccount = () => {
  const { tenant_slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { tenant } = useTenant();
  const { user } = useAuth();
  const { toast } = useToast();

  // Parse tab from URL hash or default to "home"
  const hashTab = location.hash.replace("#", "") as TabId;
  const [activeTab, setActiveTab] = useState<TabId>(
    TABS.some((t) => t.id === hashTab) ? hashTab : "home"
  );

  // Show KYC overlay when needed
  const [showKyc, setShowKyc] = useState(false);

  // Handle checkout success redirect — mark card request as pending
  useEffect(() => {
    const checkoutStatus = searchParams.get("checkout");
    const cardRequestId = searchParams.get("card_request_id");

    if (checkoutStatus === "success" && cardRequestId) {
      // Update the card request status from draft to pending (submitted for review)
      supabase
        .from("card_requests")
        .update({
          status: "pending",
          submitted_at: new Date().toISOString(),
        } as any)
        .eq("id", cardRequestId)
        .then(({ error }) => {
          if (error) console.error("Failed to update card request status:", error);
        });

      toast({ title: "Payment Successful! 🎉", description: "Your card has been submitted for review." });

      // Clean up query params
      searchParams.delete("checkout");
      searchParams.delete("session_id");
      searchParams.delete("card_request_id");
      setSearchParams(searchParams, { replace: true });
    } else if (checkoutStatus === "success") {
      toast({ title: "Payment Successful! 🎉", description: "Your card has been ordered and will be sent to you soon." });
      searchParams.delete("checkout");
      searchParams.delete("session_id");
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  useEffect(() => {
    if (!user) {
      navigate(`/t/${tenant_slug}`);
    }
  }, [user, tenant_slug, navigate]);

  const switchTab = (tab: TabId) => {
    setActiveTab(tab);
    window.location.hash = tab;
  };

  if (!tenant || !user) return null;

  return (
    <div className="flex h-[100dvh] flex-col bg-background overflow-hidden">
      {/* Top bar */}
      <header className="border-b px-4 py-3 flex items-center justify-between bg-card sticky top-0 z-30">
        <button
          onClick={() => { setShowKyc(false); switchTab("profile"); }}
          className="h-9 w-9 rounded-full bg-muted flex items-center justify-center hover:bg-muted/80 transition-colors"
        >
          <User className="h-4 w-4 text-muted-foreground" />
        </button>
        <div className="flex items-center gap-2">
          <div
            className="flex h-8 w-8 items-center justify-center rounded-lg"
            style={{ backgroundColor: `hsl(${tenant.primaryColor})` }}
          >
            <CreditCard className="h-4 w-4 text-white" />
          </div>
          <span className="font-display font-semibold text-sm">{tenant.brandName}</span>
        </div>
        <button
          onClick={() => { setShowKyc(false); switchTab("notifications"); }}
          className="h-9 w-9 rounded-full bg-muted flex items-center justify-center hover:bg-muted/80 transition-colors relative"
        >
          <Bell className="h-4 w-4 text-muted-foreground" />
          <div
            className="absolute top-1 right-1 h-2 w-2 rounded-full"
            style={{ backgroundColor: `hsl(${tenant.primaryColor})` }}
          />
        </button>
      </header>

      {/* Content area */}
      <main className="flex-1 overflow-y-auto pb-20" style={{ minHeight: 0 }}>
        {showKyc ? (
          <UserKyc tenant={tenant} user={user} onComplete={() => setShowKyc(false)} />
        ) : (
          <>
            {activeTab === "home" && (
              <UserHome tenant={tenant} user={user} onStartKyc={() => setShowKyc(true)} />
            )}
            {activeTab === "card" && <UserCard tenant={tenant} user={user} />}
            {activeTab === "profile" && <UserProfile tenant={tenant} user={user} />}
            {activeTab === "notifications" && <UserNotifications tenant={tenant} user={user} />}
          </>
        )}
      </main>

      {/* Bottom tab bar */}
      <nav className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[960px] bg-card border-t z-40 safe-area-bottom">
        <div className="flex items-center justify-around max-w-lg mx-auto">
          {TABS.map((tab) => {
            const active = activeTab === tab.id && !showKyc;
            return (
              <button
                key={tab.id}
                onClick={() => { setShowKyc(false); switchTab(tab.id); }}
                className={cn(
                  "flex flex-col items-center gap-0.5 py-2 px-4 text-[11px] transition-colors min-w-[64px]",
                  active
                    ? "font-semibold"
                    : "text-muted-foreground"
                )}
                style={active ? { color: `hsl(${tenant.primaryColor})` } : undefined}
              >
                <tab.icon className="h-5 w-5" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default UserAccount;
